import { useQuery, useQueryClient } from '@tanstack/react-query';
import { billingApi, type LinkageEligibility } from '@/services/api/billing';
import { useAuthStore } from '@/store/authStore';

/**
 * Shared React Query key for the linkage-eligibility endpoint. Exported
 * so mutations (link, disconnect, plan change) can invalidate it via
 * `queryClient.invalidateQueries({ queryKey: LINKAGE_ELIGIBILITY_QUERY_KEY })`.
 */
export const LINKAGE_ELIGIBILITY_QUERY_KEY = ['linkage-eligibility'] as const;

/**
 * Query `GET /billing/linkage-eligibility` — which "Link X" buttons the
 * UI should render.
 *
 * Backed by the same server-side check that gates
 * `POST /google-ads/link-account` and `GET /auth/meta/authorize`, so the
 * button visible → OAuth 200, button hidden → OAuth 409 desync class of
 * bugs cannot happen when both consumers read from this hook.
 *
 * Shared between `LinkAccountModal` and `Home` — both call this hook
 * and read from the same cache under `LINKAGE_ELIGIBILITY_QUERY_KEY`.
 */
export function useLinkageEligibilityQuery() {
  const { isAuthenticated } = useAuthStore();

  return useQuery<LinkageEligibility>({
    queryKey: LINKAGE_ELIGIBILITY_QUERY_KEY,
    queryFn: () => billingApi.getLinkageEligibility(),
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook helper: returns a callback that invalidates the shared eligibility
 * query. Call this from Google/Meta link and disconnect mutations'
 * `onSuccess` handlers.
 */
export function useInvalidateLinkageEligibility() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: LINKAGE_ELIGIBILITY_QUERY_KEY });
}
